import React, { useEffect, useRef, useState } from "react";
import PageTitle from "../../shared/components/pagetitle";
import {
  CHEVRON_LEFT,
  CLOSE,
  EMPTY_SEARCH_2,
  SEARCH_INPUT_ICON,
} from "../../assets";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useQuery } from "react-query";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import {
  getActivityLogsUnderCourt,
  getCourtSearchStats,
  getCourtSinglectivityLogs,
} from "../../Services/court.services";
import { CustomToolTip } from "../admin/activityLogs";
import { Case } from "../../types";
import ButtonV2 from "../../shared/components/buttonV2";
import { useAppSelector } from "../../hooks";
import { selectUser } from "../../states/slices/authReducer";

const COLORS = ["#00A63E", "#C4A605"]; // Green and Gold

const renderCustomizedLabel = ({ percent }: any) =>
  `${(percent * 100).toFixed(0)}%`;

function ActivityLogs() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const user = useAppSelector(selectUser);
  const { data: activityLogs, isLoading } = useQuery(
    ["logs", currentPage],
    () => getActivityLogsUnderCourt(currentPage),
    {
      refetchOnWindowFocus: false,
    }
  );
  const [selectedActivityLogId, setSelectedActivityLogId] = useState<
    number | null
  >(null);

  const { data: activityLogsDetails, isLoading: loadingDetails } = useQuery(
    ["activity-details", selectedActivityLogId],
    () => getCourtSinglectivityLogs(selectedActivityLogId),
    {
      enabled: selectedActivityLogId !== null,
    }
  );
  const pagesRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { data: caseStats } = useQuery("stats", getCourtSearchStats);

  const data = [
    { name: "Total Upload", value: caseStats?.total_uploaded },
    { name: "Total Update", value: caseStats?.total_updated },
  ];

  useEffect(() => {
    const selectedButton = pagesRefs.current[currentPage];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentPage]);

  return (
    <div className="p-3 sm:p-5">
      <PageTitle
        title="Activity Logs"
        subtitle="Track your searches and manage overall activities"
      />
      <div className="w-full flex flex-col lg:flex-row items-start gap-4 lg:gap-x-2 mt-5">
        <div className="w-full lg:w-[400px] border h-full border-borderColor rounded-xl">
          <div className="w-full flex items-center justify-between p-3 bg-lightGold rounded-t-xl">
            <span className="text-sm sm:text-[16px] font-medium text-[#2A2E34]">
              Summary
            </span>
          </div>
          <div className="relative p-4 sm:p-6">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={data}
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={renderCustomizedLabel}
                  labelLine={false}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomToolTip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-[#2A2E34]">
                  {(caseStats?.total_uploaded || 0) +
                    (caseStats?.total_updated || 0)}
                </div>
                <div className="text-xs sm:text-sm text-gray-500">
                  Total Activities
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 pb-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#00A63E] rounded-full"></div>
                <span className="text-xs sm:text-sm text-gray-600">
                  Total Upload: {caseStats?.total_uploaded || 0}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#C4A605] rounded-full"></div>
                <span className="text-xs sm:text-sm text-gray-600">
                  Total Update: {caseStats?.total_updated || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full border border-borderColor rounded-xl">
          <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center px-3 sm:px-4 py-3 gap-4">
            <span className="text-sm sm:text-[16px] font-semibold text-[#2A2E34]">
              Activity Logs
            </span>
            <div className="w-full sm:w-fit items-center gap-x-2 flex px-3 py-2 rounded-xl border border-borderColor">
              <img
                src={SEARCH_INPUT_ICON}
                className="w-4 h-4 sm:w-[20px] sm:h-[20px] flex-shrink-0"
                alt=""
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent placeholder:text-sm outline-none"
                placeholder="Search activity logs"
              />
            </div>
          </div>

          <div className="mt-2">
            {activityLogs?.data?.length > 0 ? (
              <div>
                {activityLogs?.data
                  ?.filter((item: any) =>
                    item.description
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase())
                  )
                  ?.map((item: any) => (
                    <div
                      key={item.id}
                      className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border-b border-borderColor gap-3"
                    >
                      <div className="flex flex-col gap-y-1 w-full">
                        <span className="text-sm font-medium text-[#2A2E34] break-words">
                          {item.description}
                        </span>
                        <span className="text-xs font-normal text-primary-50">
                          {item.created_at}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2 flex-shrink-0">
                        <button
                          onClick={() => setSelectedActivityLogId(item.id)}
                          className="bg-globalGold hover:scale-95 transition-all duration-300 px-3 py-1 rounded-lg"
                        >
                          <span className="text-xs font-medium text-white">
                            Review
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}

                <div className="flex flex-col sm:flex-row items-center justify-between pt-3 px-3 sm:px-5 py-4 gap-4">
                  <div className="flex items-center space-x-2 sm:space-x-5">
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(activityLogs?.meta?.current_page - 1)
                      }
                      disabled={activityLogs?.meta?.current_page === 1}
                      className={`border border-[#EBEBEB] rounded-xl w-8 h-8 sm:w-[40px] sm:h-[40px] flex items-center gap-x-2 justify-center ${
                        activityLogs?.meta?.current_page === 1 && "opacity-50"
                      }`}
                    >
                      <img
                        src={CHEVRON_LEFT}
                        className="w-4 h-4 sm:w-[20px] sm:h-[20px]"
                        alt=""
                      />
                    </button>

                    <div
                      className={`flex items-center gap-x-1 sm:gap-x-2 ${
                        activityLogs?.meta?.links?.slice(1, -1).length > 5 &&
                        "max-w-[150px] sm:max-w-[200px] overflow-x-auto"
                      }`}
                    >
                      {activityLogs?.meta?.links
                        ?.slice(1, -1)
                        .map((page: any, index: number) => (
                          <button
                            key={index}
                            ref={(el) => (pagesRefs.current[index] = el)}
                            onClick={() => {
                              page.url && setCurrentPage(Number(page.label));
                            }}
                            className={`flex items-center justify-center pt-[0.5px] hover:bg-globalGold hover:text-white transition-all duration-300 gap-x-3 min-w-[32px] h-[32px] sm:min-w-[40px] sm:h-[40px] rounded-xl ${
                              page.active
                                ? "bg-globalGold text-white"
                                : "border text-[#2A2E34]"
                            }`}
                          >
                            <span className="text-xs sm:text-[13px]">
                              {page.label}
                            </span>
                          </button>
                        ))}
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(activityLogs?.meta?.current_page + 1)
                      }
                      disabled={
                        activityLogs?.meta?.current_page ===
                        activityLogs?.meta?.last_page
                      }
                      className={`border border-[#EBEBEB] rounded-xl gap-x-2 w-8 h-8 sm:w-[40px] sm:h-[40px] flex items-center justify-center ${
                        activityLogs?.meta?.current_page ===
                          activityLogs?.meta?.last_page && "opacity-50"
                      }`}
                    >
                      <img
                        src={CHEVRON_LEFT}
                        className="w-4 h-4 sm:w-[20px] sm:h-[20px] scale-x-[-1]"
                        alt=""
                      />
                    </button>
                  </div>
                </div>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 color="#9ca3af" size={30} className="animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-y-2 pb-5 justify-center items-center w-full px-4">
                <img
                  src={EMPTY_SEARCH_2}
                  className="w-24 h-20 sm:w-[147.69px] sm:h-[120px]"
                  alt=""
                />
                <span className="text-lg sm:text-xl font-bold text-customDark text-center">
                  No Activities Logs Yet
                </span>
                <span className="text-sm font-medium text-primary-50 max-w-sm text-center">
                  You will see you activities here.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        <div
          className={`w-full h-full fixed ${
            selectedActivityLogId ? "block" : "hidden"
          } top-0 left-0 bg-black flex items-center justify-center bg-opacity-50 z-[999] p-4`}
        >
          {selectedActivityLogId && (
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="w-full max-w-[600px] max-h-[90vh] bg-white rounded-xl shadow-xl z-50 overflow-hidden"
            >
              <div className="w-full flex items-center justify-between bg-[#00A63E1A] p-3 sm:p-5">
                <h2 className="text-sm sm:text-[16px] font-semibold text-[#2A2E34]">
                  Record Details
                </h2>
                <button
                  onClick={() => setSelectedActivityLogId(null)}
                  className="w-[30px] h-[30px] rounded-full -mt-2 flex items-center justify-center hover:bg-gray-200 flex-shrink-0"
                >
                  <img src={CLOSE} className="w-[20px] h-[20px]" alt="" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center mt-10 p-8">
                  <Loader2 color="#9ca3af" className="animate-spin" size={30} />
                </div>
              ) : (
                <div className="p-3 sm:p-5 max-h-[300px] overflow-y-scroll flex flex-col gap-y-5">
                  {activityLogsDetails?.user?.cases?.map((item: Case) => (
                    <div
                      key={item.id}
                      className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 border border-borderColor rounded-xl p-3 sm:p-5"
                    >
                      <div className="flex flex-col items-start">
                        <span className="text-xs sm:text-sm font-medium text-primary-50">
                          Property Title (certificate of occupancy)
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-customDark break-words">
                          {item.title}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-xs sm:text-sm font-medium text-primary-50">
                          Registered Title number
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-customDark break-words">
                          {item.title_number}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-xs sm:text-sm font-medium text-primary-50">
                          Location of Property
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-customDark break-words">
                          {item.address}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-xs sm:text-sm font-medium text-primary-50">
                          Survey plan number
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-customDark break-words">
                          {item.survey_plan_number}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-xs sm:text-sm font-medium text-primary-50">
                          Name of Owner of property (optional)
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-customDark break-words">
                          {item.owner_name}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-xs sm:text-sm font-medium text-primary-50">
                          Case Status
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-customDark">
                          {item.status}
                        </span>
                      </div>
                      <div className="flex flex-col items-start lg:col-span-2">
                        <span className="text-xs sm:text-sm font-medium text-primary-50">
                          Last Updated
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-customDark">
                          {item.updated_at}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-end justify-end gap-x-3 p-4 sm:p-8">
                <ButtonV2
                  title={"Close"}
                  btnStyle={
                    "w-full sm:w-fit h-fit px-6 sm:px-7 py-2 rounded-xl bg-globalGold"
                  }
                  textStyle={"text-sm font-medium text-white"}
                  handleClick={() => setSelectedActivityLogId(null)}
                />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityLogs;
